import { Users } from "lucide-react";
import { PageHeading, Section, Field, Steps, Callout, RoleBadge, Diagram, MockRow, MockField, MockSelect, MockButton } from "../shared";

export default function AdminActorsSection() {
  return (
    <div data-testid="guide-admin-actors">
      <PageHeading
        icon={Users}
        title="Authority Actors"
        description="The reviewers and sign-off authorities attached to a lane. An actor is a name plus the powers they hold in that lane — can review, can sign off, or both."
      />

      <Callout variant="info" title="In plain words">
        If a lane is a track, actors are the people standing along it who check and approve the work. For each lane you list who is allowed to review and who is allowed to give the final sign-off.
      </Callout>

      <Section title="Who can manage it">
        <div className="flex items-center gap-2"><RoleBadge role="super_admin" /><span className="text-xs text-white/55"><span className="text-white">manage_governance</span> on a draft version.</span></div>
      </Section>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it when a lane needs a reviewer or sign-off authority added, changed, or removed. Make sure a matching Team Member name exists first, so the system can route notification emails automatically.
        </p>
      </Section>

      <Section title="Adding an actor">
        <Diagram
          frameLabel="Admin → Actors (on a draft version)"
          legend={[
            <>Click <span className="text-white">Add Actor</span>.</>,
            <>Pick the <span className="text-white">lane</span> this actor serves.</>,
            <>Set the actor key and a <span className="text-white">display name</span> that matches a Team Member name.</>,
            <>Tick <span className="text-white">Can review</span> and/or <span className="text-white">Can sign off</span>, then Save.</>,
          ]}
        >
          <MockButton label="Add Actor" marker={1} />
          <MockSelect label="Lane" value="SDF MD Lane" marker={2} />
          <MockRow>
            <MockField label="Actor key" value="SDF_MD" />
            <MockField label="Display name" value="SDF MD" marker={3} />
          </MockRow>
          <MockRow>
            <MockField label="Can review" value="Yes" marker={4} />
            <MockField label="Can sign off" value="Yes" />
          </MockRow>
          <MockButton label="Save" />
        </Diagram>
        <Steps steps={[
          <>Make sure a Team Member with the same name exists (Admin → Team Members) so notifications can route by email.</>,
          <>On a draft version, Actors tab → <span className="text-white">Add Actor</span>.</>,
          <>Pick the lane, set the actor key and display name, tick the rights this actor needs.</>,
          <>Save. Activate the draft from Versions when ready.</>,
        ]} />
      </Section>

      <Section title="Actor fields">
        <div className="space-y-2">
          <Field name="Actor key" required="required"><p>Stable internal identifier.</p></Field>
          <Field name="Display name" required="required"><p>Should match a Team Member name when possible — that is what makes the email/notification routing work without manual mapping.</p></Field>
          <Field name="Lane" required="required"><p>Which lane this actor serves.</p></Field>
          <Field name="Can review" required="optional"><p>If on, this actor appears in the Review-required dropdown and can act on Send-for-review tasks.</p></Field>
          <Field name="Can sign off" required="optional"><p>If on, this actor can grant the final sign-off for items in this lane.</p></Field>
          <Field name="Active" required="optional"><p>Inactive actors leave dropdowns but stay on historical items.</p></Field>
        </div>
      </Section>

      <Callout variant="info" title="SDF MD / PMO / Board Secretary are special">
        These three identities are bound at the user level (not via the matrix or actors). They route by role-binding regardless of any actor configuration here. Use Actors for everyone else.
      </Callout>
    </div>
  );
}
